import React from "react";
import Header from "../components/layout/header/Header";
import Footer from "../components/layout/footer/Footer";
import "../App.css";
import "../components/layout/privacy and policy/terms.css";
import { Helmet } from "react-helmet-async";

const pageTitle = "Terms & Conditions | AIGIO";
const pageDescription = "Understand our terms and conditions for using AIGIO services.";

type Block =
  | { type: "p"; text: string }
  | { type: "callout"; text: string }
  | { type: "list"; items: string[] };

type Section = {
  id: string;
  title: string;
  number?: string; // Added to explicitly handle legal document numbering
  blocks: Block[];
};

const LAST_UPDATED = "July 1, 2026";

const SECTIONS: Section[] = [
  {
    id: "introduction",
    title: "Introduction",
    blocks: [
      {
        type: "p",
        text: `These Terms & Conditions (“Terms”) govern your use of AIGIO (the “Service”), a product of Orglife Private Limited (CIN U93090GJ2017PTC097878), a company incorporated in India with its registered office at B-3/202, Lahabita, Nr. Aiyana Complex, Opp. Shukun Bungalows, Off S. G. Highway, Thaltej, Ahmedabad, Gujarat, India – 380054. By creating an account, subscribing, or using the Service, you agree to these Terms. If you do not agree, please do not use the Service.`,
      },
    ],
  },
  {
    id: "service",
    title: "The Service",
    number: "1",
    blocks: [
      {
        type: "p",
        text: `AIGIO is an AI-powered brand strategy platform. It guides you through an intake, generates a research brief, and produces brand identity, strategy, content, and website/social audits using artificial intelligence. Outputs are drafts and suggestions intended to assist your work — they are not professional, legal, financial, or guaranteed business advice.`,
      },
    ],
  },
  {
    id: "eligibility",
    title: "Eligibility & Accounts",
    number: "2",
    blocks: [
      {
        type: "list",
        items: [
          `The Service does not impose a strict minimum age. However, if you are a minor under the laws applicable to you, you may use the Service only with the involvement and consent of a parent or legal guardian, who accepts these Terms on your behalf and is responsible for all activity and payments.`,
          "You are responsible for the accuracy of the information you provide and for keeping your login credentials secure",
          "You are responsible for all activity that occurs under your account.",
        ],
      },
    ],
  },
  {
    id: "subscriptions",
    title: "Subscriptions, Credits, and Payment",
    number: "3",
    blocks: [
    
      {
        type: "list",
        items: [
           `The Service is offered on a paid subscription basis. There is no free plan or trial. Plans, prices, included credits, and\nlimits are shown in the app and at checkout — see our Pricing page`,
          "Each subscription includes credits; features consume credits as shown before you confirm. We do not sell separate add-on credits",
          "Website Audit and Social Audit are available for a one-time charge, limited to once per IP address.",
          "Subscriptions renew automatically at the end of each billing cycle until you cancel. You authorise us and our payment processor to charge your payment method on each renewal until cancellation",
          "Payments are processed by Razorpay. We do not store full card details.",
          "Fees may be charged in Indian Rupees (INR) or your local currency as shown at checkout, and are exclusive of applicable taxes unless stated.",
          "Cancellations and refunds are governed by the Refund & Cancellation Policy.",
        ],
      },
    ],
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    number: "4",
    blocks: [
      {
        type: "p",
        text: `You agree not to:`,
      },
      {
        type: "list",
        items: [
          " use the Service for unlawful, infringing, deceptive, or harmful purposes.",
          "Generate content that is illegal, defamatory, hateful, or violates the rights of others",
          "Attempt to reverse engineer, scrape, overload, or disrupt the Service or its infrastructure.",
          "Share, resell, or redistribute access to the Service without our written permission.",
          "Use the Service to build a competing product, including by extracting our prompts, systems, or models.",
        ],
      },
    ],
  },
  {
    id: "ownership",
    title: "Your Content & Ownership of Outputs",
    number: "5",
    blocks: [
      {
        type: "list",
        items: [
          `You retain ownership of the inputs you provide (intake answers, brand information, and uploaded material).`,
          `Subject to your payment and compliance with these Terms, you own and may commercially use the outputs generated for your brand through the Service.`,
          `AI outputs may be non-unique; similar outputs could be generated for other users. We make no claim of exclusivity over generated material, and you are responsible for reviewing outputs before use.`,
          `You grant us a limited licence to process your inputs solely to operate, secure, and improve the Service.`,
        ],
      },
    ],
  },
  {
    id: "ai-limitations",
    title: "AI Limitations & Disclaimer",
    number: "6",
    blocks: [
      {
        type: "p",
        text: `AI outputs can be inaccurate, incomplete, or outdated. You are solely responsible for reviewing, verifying, and deciding whether to rely on any output.The Service is provided on an "as is" and "as available" basis without warranties of any kind, to the maximum extent permitted by law.`,
      },
    ],
  },
  {
    id: "third-party-services",
    title: "Third-Party Services",
    number: "7",
    blocks: [
      {
        type: "p",
        text: `The Service relies on third parties such as our payment gateway, cloud hosting, analytics, and AI processing providers.Your use of those features may also be subject to those providers’ terms. We are not responsible for third-party servicesoutside our control.`,
      },
    ],
  },
  {
    id: "limitation-of-liability",
    title: "Limitation of Liability",
    number: "8",
    blocks: [
      {
        type: "p",
        text: `To the maximum extent permitted by law, Orglife Private Limited will not be liable for any indirect, incidental, special, or consequential damages, or loss of profits, data, or goodwill, arising from your use of the Service. Our total aggregate liability for any claim is limited to the amount you paid us for the Service in the 3 months preceding the event giving rise to the claim.`,
      },
    ],
  },
  {
    id: "termination",
    title: "Suspension & Termination",
    number: "9",
    blocks: [
      {
        type: "p",
        text: `We may suspend or terminate your access if you breach these Terms or use the Service in a way that risks harm to us or others. You may stop using the Service and cancel your subscription at any time. Unused amounts are handled per our Refund & Cancellation Policy.`,
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to These Terms",
    number: "10",
    blocks: [
      {
        type: "p",
        text: `We may update these Terms from time to time. Material changes are reflected by the “Last updated” date above.Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.`,
      },
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law & Jurisdiction",
    number: "11",
    blocks: [
      {
        type: "p",
        text: `These Terms are governed by the laws of India. Any disputes are subject to the exclusive jurisdiction of the courts of Ahmedabad, Gujarat, India.`,
      },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    number: "12",
    blocks: [
      {
        type: "p",
        text: `For any questions regarding these Terms, contact support@aigio.ai.`,
      },
    ],
  },
];

function renderBlock(block: Block, key: number) {
  switch (block.type) {
    case "callout":
      return (
        <div key={key} className="legal-callout">
          "{block.text}"
        </div>
      );
    case "list":
      return (
        <ul
          key={key}
          className="legal-list"
          style={{ listStyleType: "disc", paddingLeft: "24px", margin: "12px 0" }}
        >
          {block.items.map((item, i) => (
            <li key={i} style={{ marginBottom: "8px", display: "list-item" }}>
              {item}
            </li>
          ))}
        </ul>
      );
    default:
      return (
        <p key={key} className="legal-paragraph">
          {block.text}
        </p>
      );
  }
}

const TermsAndConditions: React.FC = () => {
  const [activeId, setActiveId] = React.useState(SECTIONS[0].id);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: 0 },
    );

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <div className="page-shell page-shell--legal">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <Header />

        <main className="terms-page">
          <div className="legal-page__inner">
            <p className="legal-page__kicker">LEGAL DOCUMENTATION</p>
            <h1 className="legal-page__title">Terms and Conditions</h1>
            <p className="legal-page__updated">
              Last Updated: {LAST_UPDATED}. AIGIO is a product of Orglife
              Private Limited
            </p>

            <div className="legal-page__body">
              <aside className="legal-toc" aria-label="Table of contents">
                <ul className="legal-toc__list">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`legal-toc__link ${
                          activeId === s.id ? "legal-toc__link--active" : ""
                        }`}
                      >
                        {s.number ? `${s.number}. ` : ""}{s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="legal-content">
                {SECTIONS.map((s) => (
                  <section key={s.id} id={s.id} className="legal-section">
                    <h2 className="legal-section__title">
                      {s.number ? `${s.number}. ` : ""}{s.title}
                    </h2>
                    {s.blocks.map((b, j) => renderBlock(b, j))}
                  </section>
                ))}

                <p className="legal-page__contact">
                  If you have any questions about these Terms, please contact
                  our legal department.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default TermsAndConditions;